import React, { useCallback } from 'react'
import { useAccount } from '@azure/msal-react'
import {
  type AccountInfo,
  type AuthenticationResult,
  type EventMessage,
  EventType,
  type IPublicClientApplication
} from '@azure/msal-browser'

const getConfiguredTenantId = (
  instance: IPublicClientApplication
): string | undefined => {
  try {
    const authority = instance.getConfiguration().auth.authority
    if (!authority) return undefined
    const segment = authority.replace(/\/+$/, '').split('/').pop()
    return segment?.toLowerCase()
  } catch {
    return undefined
  }
}

const selectAccount = (
  instance: IPublicClientApplication,
  accounts: AccountInfo[]
): AccountInfo | undefined => {
  const active = instance.getActiveAccount()
  if (active) return active
  if (accounts.length <= 1) return accounts[0]

  const tenantId = getConfiguredTenantId(instance)
  const isSpecificTenant =
    tenantId !== undefined &&
    tenantId !== 'common' &&
    tenantId !== 'organizations' &&
    tenantId !== 'consumers'

  if (isSpecificTenant) {
    const match = accounts.find((candidate) => {
      const claimTid = candidate.idTokenClaims?.tid
      return (
        candidate.tenantId?.toLowerCase() === tenantId ||
        (typeof claimTid === 'string' && claimTid.toLowerCase() === tenantId)
      )
    })
    if (match) return match
  }

  return accounts[0]
}

export const useActiveAccount = (
  instance: IPublicClientApplication,
  accounts: AccountInfo[]
): AccountInfo | undefined => {
  // The most reliable signal for which account a user just authenticated with
  // is the event payload from MSAL. Registering this callback is the documented
  // canonical way to keep the active account in sync across multiple accounts.
  React.useEffect(() => {
    const callbackId = instance.addEventCallback(
      (message: EventMessage) => {
        const payload = message.payload as AuthenticationResult | null
        if (payload?.account) {
          instance.setActiveAccount(payload.account)
        }
      },
      [EventType.LOGIN_SUCCESS, EventType.ACQUIRE_TOKEN_SUCCESS]
    )
    return () => {
      if (callbackId) instance.removeEventCallback(callbackId)
    }
  }, [instance])

  const getSelectedAccount = useCallback(
    () => selectAccount(instance, accounts),
    [instance, accounts]
  )

  // Fallback for cold starts with several cached accounts and no fresh login
  // event: deterministically set the active account matching the app's tenant.
  React.useEffect(() => {
    const selected = getSelectedAccount()
    const active = instance.getActiveAccount()
    if (selected && selected.homeAccountId !== active?.homeAccountId) {
      instance.setActiveAccount(selected)
    }
  }, [instance, accounts, getSelectedAccount])

  // Use the reactive useAccount hook so the account (and its claims) update when
  // MSAL refreshes it, falling back to the deterministic selection.
  const selectedAccount = getSelectedAccount()
  return (
    useAccount(
      selectedAccount
        ? { homeAccountId: selectedAccount.homeAccountId }
        : undefined
    ) ?? selectedAccount
  )
}
